// PidBagModal.jsx — Add/Edit surface for PID Labels (PIDBags) on the shared PidModal
// scaffold. One body, two modes (Create/Edit identical by construction — plan decision 2):
// editable label fields (they are real PIDBags columns, unlike Methods), images, and the
// commodity cards (styles only — no size level on labels).
// Save flows preserved from the retired panels:
//   create — POST /api/pidbags per commodity; images upload to the first created row
//   edit   — per-commodity reconcile (PUT kept / POST new / DELETE removed), mark-delete
//            images on the group's primary row, staged uploads to a stable kept row
import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Undo2, X } from 'lucide-react';
import styled from 'styled-components';
import authAxios from '../utils/authAxios';
import SecureImage from './SecureImage';
import PidModal, {
  PrimaryButton, QuietButton,
  MSection, MSectionTitle, MSectionNote,
  FieldGrid, FieldWrap, FieldLabel, FieldInput, FieldError,
  ImgGrid, ImgWrap, ImgActionBtn,
} from './PidModal';
import CommodityCardsEditor from './CommodityCardsEditor';
import { ConfirmDelete } from '../styles/TableStyles';
import {
  digitsOnly,
  validateEAN8,
  validateEAN13,
  validateUPC12,
  preventNonDigitsOnKeyDown,
  handlePasteDigitsOnly,
} from '../utils/barcodeUtils';

const Img = styled(SecureImage)`
  display: block;
  width: 100%;
  height: 110px;
  object-fit: cover;
`;

const PreviewImg = styled.img`
  display: block;
  width: 100%;
  height: 110px;
  object-fit: cover;
`;

const FileInput = styled.input`
  font: inherit;
`;

const FIELDS = [
  ['Label ID', 'LabelID'],
  ['Type', 'Type'],
  ['Retailer', 'Retailer'],
  ['Item Number', 'ItemNumber'],
  ['Item Description', 'Item_Description'],
  ['UPC', 'UPC'],
];

function normalizePidBagFilename(input) {
  if (!input) return '';
  let value = String(input).trim();
  if (!value) return '';

  if (/%[0-9A-Fa-f]{2}/.test(value)) {
    try { value = decodeURIComponent(value); } catch {}
  }

  try {
    const url = new URL(value);
    value = url.pathname || '';
  } catch {
    // Plain filename or relative path.
  }

  value = value.replace(/^.*\/vault-images\//i, '');
  value = value.replace(/^\/+/, '').replace(/^PIDBags\//i, '');
  value = value.split('?')[0].split('#')[0];

  const parts = value.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : '';
}

const validUpcEan = (digits) =>
  (digits.length === 8 && validateEAN8(digits)) ||
  (digits.length === 12 && validateUPC12(digits)) ||
  (digits.length === 13 && validateEAN13(digits));

const PidBagModal = ({ mode, groupData, commodities, packTypes = [], onClose }) => {
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState(() => (isEdit
    ? {
        ItemNumber: groupData?.baseInfo?.ItemNumber || '',
        Retailer: groupData?.baseInfo?.Retailer || '',
        LabelID: groupData?.baseInfo?.LABEL_ID || groupData?.labelId || '',
        Item_Description: groupData?.baseInfo?.Item_Description || '',
        Type: groupData?.baseInfo?.Type || '',
        UPC: groupData?.baseInfo?.UPC || '',
      }
    : { ItemNumber: '', Retailer: '', LabelID: '', Item_Description: '', Type: '', UPC: '' }));

  const [commodityGroups, setCommodityGroups] = useState([]); // cards shape: [{commodity, styleGroups:[{style}]}]
  const [existingImages, setExistingImages] = useState([]);   // persisted filenames (edit)
  const [toDelete, setToDelete] = useState(new Set());        // normalized filenames marked for deletion
  const [newFiles, setNewFiles] = useState([]);               // File[] staged until Save
  const [isSaving, setIsSaving] = useState(false);
  const [upcError, setUpcError] = useState('');

  const commodityOptions = useMemo(
    () => commodities.map(c => ({ value: c.UniqueID, label: c.Commodity })),
    [commodities]
  );

  const primaryRowId = useMemo(
    () => (isEdit ? (groupData?.rows?.[0]?.UniqueID || null) : null),
    [isEdit, groupData]
  );

  /* ---------- hydrate cards (edit mode) ---------- */
  useEffect(() => {
    if (!isEdit || !groupData) return;
    const styleByLabel = new Map(packTypes.map(o => [o.label, o]));
    const arr = [];
    for (const [name, styles] of Object.entries(groupData.commodities || {})) {
      const opt = commodityOptions.find(c => c.label === name) || { value: null, label: name };
      const styleGroups = (styles || [])
        .map(s => styleByLabel.get(String(s)))
        .filter(Boolean)
        .map(style => ({ style, sizes: [] }));
      arr.push({ commodity: opt, styleGroups });
    }
    setCommodityGroups(arr);
  }, [isEdit, groupData, packTypes, commodityOptions]);

  /* ---------- images ---------- */
  useEffect(() => {
    if (!primaryRowId) { setExistingImages([]); return; }
    (async () => {
      try {
        const res = await authAxios.get(`/api/pidbags/images/${primaryRowId}`);
        setExistingImages(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error loading bag images:', err);
        setExistingImages([]);
      }
    })();
  }, [primaryRowId]);

  const toggleMarkDelete = (src) => {
    const fn = normalizePidBagFilename(src);
    if (!fn) return;
    setToDelete(prev => {
      const next = new Set(prev);
      if (next.has(fn)) next.delete(fn); else next.add(fn);
      return next;
    });
  };

  const handleAddFiles = (e) => {
    if (e.target.files?.length) setNewFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const previews = useMemo(() => newFiles.map(f => ({ url: URL.createObjectURL(f), name: f.name })), [newFiles]);
  useEffect(() => () => previews.forEach(p => URL.revokeObjectURL(p.url)), [previews]);

  const uploadFiles = async (targetRowId) => {
    if (newFiles.length === 0 || !targetRowId) return;
    const uploaded = [];
    for (const file of newFiles) {
      const fd = new FormData();
      const cleanLabel = String(formData.LabelID || 'Bag')
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-_]/g, '');
      fd.append('image', file);
      fd.append('name', cleanLabel);
      const up = await authAxios.post('/uploadImage/PIDBags', fd);
      const blobName = up?.data?.blobName || '';
      const filenameOnly = blobName.split('/').pop();
      if (filenameOnly) uploaded.push(filenameOnly);
    }
    if (uploaded.length > 0) {
      await authAxios.post('/api/pidbags/images', { PIDBagID: targetRowId, ImagePaths: uploaded });
    }
  };

  /* ---------- UPC field ---------- */
  const handleUPCChange = (e) => {
    const value = digitsOnly(e.target.value);
    let error = '';
    if (value.length > 0) {
      if (![8, 12, 13].includes(value.length)) error = 'UPC/EAN must be 8, 12, or 13 digits';
      else if (!validUpcEan(value)) error = 'Invalid check digit';
    }
    setUpcError(error);
    setFormData(prev => ({ ...prev, UPC: value }));
  };

  /* ---------- save ---------- */
  const handleSave = async () => {
    if (!String(formData.ItemNumber).trim() || !String(formData.LabelID).trim()) {
      alert('Item Number and Label ID are required.');
      return;
    }
    if (commodityGroups.length === 0) {
      alert('Add at least one commodity.');
      return;
    }
    const upcDigits = digitsOnly(formData.UPC);
    if (upcDigits && !validUpcEan(upcDigits)) {
      alert('UPC/EAN is invalid.');
      return;
    }

    setIsSaving(true);
    try {
      const payloadFor = (group) => ({
        ItemNumber: formData.ItemNumber,
        Retailer: formData.Retailer,
        Commodity: group.commodity?.value || null,
        LABEL_ID: formData.LabelID,
        Item_Description: formData.Item_Description,
        Styles: (group.styleGroups || []).map(sg => sg.style?.value).filter(v => v !== null && v !== undefined).join(','),
        Type: formData.Type,
        UPC: upcDigits,
      });

      if (isEdit) {
        // Reconcile rows by commodity name (the retired panel's flow, unchanged).
        const existingByCommodity = new Map();
        for (const r of groupData.rows || []) existingByCommodity.set(String(r.Commodity).trim(), r);
        const selectedNames = new Set(commodityGroups.map(g => g.commodity?.label));

        const keptRowIds = [];
        for (const group of commodityGroups) {
          const commName = group.commodity?.label;
          if (existingByCommodity.has(commName)) {
            const row = existingByCommodity.get(commName);
            await authAxios.put(`/api/pidbags/${row.UniqueID}`, payloadFor(group));
            keptRowIds.push(row.UniqueID);
          } else {
            const res = await authAxios.post('/api/pidbags', payloadFor(group));
            if (res.data?.UniqueID) keptRowIds.push(res.data.UniqueID);
          }
        }
        for (const [commName, row] of existingByCommodity.entries()) {
          if (!selectedNames.has(commName)) {
            try { await authAxios.delete(`/api/pidbags/${row.UniqueID}`); } catch {}
          }
        }

        if (primaryRowId && toDelete.size > 0) {
          for (const fn of toDelete) {
            const filename = normalizePidBagFilename(fn);
            if (!filename) continue;
            const encoded = encodeURIComponent(filename);
            try { await authAxios.delete(`/deleteImage/bags/${encoded}`); } catch {}
            try { await authAxios.delete(`/api/pidbags/images/${primaryRowId}`, { params: { filename } }); } catch {}
          }
        }
        const targetRowId = keptRowIds.includes(primaryRowId) ? primaryRowId : (keptRowIds[0] || null);
        await uploadFiles(targetRowId);
      } else {
        // Create one row per commodity card; images go to the first created row.
        const createdRows = [];
        for (const group of commodityGroups) {
          const res = await authAxios.post('/api/pidbags', payloadFor(group));
          if (res.data?.UniqueID) createdRows.push(res.data);
        }
        await uploadFiles(createdRows[0]?.UniqueID || null);
      }

      onClose();
    } catch (err) {
      console.error('Error saving PID Label:', err?.response?.data || err?.message, err);
      const data = err?.response?.data;
      const serverMsg = data?.error || data?.message;
      const extra = [data?.details, data?.code].filter(Boolean).join(' ');
      alert(serverMsg ? `Save failed: ${serverMsg}${extra ? ` (${extra})` : ''}` : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupData?.labelId) return;
    setIsSaving(true);
    try {
      // ?labelId=, not a path segment — the Azure front end decodes %2F, so labels with '/' 404
      await authAxios.delete(`/api/pidbags/by-label?labelId=${encodeURIComponent(groupData.labelId)}`);
      onClose();
    } catch (err) {
      console.error('Error deleting PIDBag group:', err);
      alert('Failed to delete group. Check dependencies.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PidModal
      title={isEdit ? `Edit PID Label — ${groupData?.baseInfo?.LABEL_ID || groupData?.labelId}` : 'New PID Label'}
      status={isEdit ? groupData?.rows?.[0]?.Status : 'Pending'}
      onClose={onClose}
      footerLeft={isEdit ? (
        <ConfirmDelete onConfirm={handleDeleteGroup} label="Delete Group" disabled={isSaving} />
      ) : null}
      footerRight={(
        <>
          <QuietButton type="button" onClick={onClose} disabled={isSaving}>Cancel</QuietButton>
          <PrimaryButton type="button" onClick={handleSave} disabled={isSaving || Boolean(upcError)}>Save</PrimaryButton>
        </>
      )}
    >
      <MSection>
        <MSectionTitle>Label Details</MSectionTitle>
        <FieldGrid>
          {FIELDS.map(([label, name]) => (
            <FieldWrap key={name}>
              <FieldLabel htmlFor={`bag-${name}`}>{label}</FieldLabel>
              {name === 'UPC' ? (
                <>
                  <FieldInput
                    id="bag-UPC"
                    name="UPC"
                    value={formData.UPC || ''}
                    onChange={handleUPCChange}
                    onKeyDown={preventNonDigitsOnKeyDown}
                    onPaste={(e) => handlePasteDigitsOnly(e, (d) => setFormData(prev => ({ ...prev, UPC: d })))}
                    placeholder="Digits only (8/12/13)"
                    inputMode="numeric"
                  />
                  {upcError && <FieldError>{upcError}</FieldError>}
                </>
              ) : (
                <FieldInput
                  id={`bag-${name}`}
                  name={name}
                  value={formData[name] || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, [name]: e.target.value }))}
                />
              )}
            </FieldWrap>
          ))}
        </FieldGrid>
      </MSection>

      <MSection>
        <MSectionTitle>
          Images {toDelete.size ? `(marked for delete: ${toDelete.size})` : ''}
        </MSectionTitle>
        {isEdit && (
          existingImages.length === 0 ? (
            <MSectionNote>No uploaded images.</MSectionNote>
          ) : (
            <ImgGrid style={{ marginBottom: 8 }}>
              {existingImages.map(src => {
                const fn = normalizePidBagFilename(src);
                if (!fn) return null;
                const marked = toDelete.has(fn);
                return (
                  <ImgWrap key={`${src}-${fn}`} $marked={marked}>
                    <Img src={fn} folder="PIDBags" alt={fn} />
                    <ImgActionBtn
                      $danger={marked}
                      title={marked ? 'Undo delete' : 'Mark for delete'}
                      onClick={() => toggleMarkDelete(src)}
                    >
                      {marked ? <Undo2 size={16} /> : <Trash2 size={16} />}
                    </ImgActionBtn>
                  </ImgWrap>
                );
              })}
            </ImgGrid>
          )
        )}
        <FileInput
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,image/png,image/jpeg,image/webp"
          onChange={handleAddFiles}
        />
        {previews.length > 0 && (
          <>
            <MSectionNote style={{ marginTop: 8 }}>These upload when you click Save.</MSectionNote>
            <ImgGrid style={{ marginTop: 8 }}>
              {previews.map((p, idx) => (
                <ImgWrap key={p.url}>
                  <PreviewImg src={p.url} alt={p.name} />
                  <ImgActionBtn
                    title="Remove from upload"
                    onClick={() => setNewFiles(prev => prev.filter((_, i) => i !== idx))}
                  >
                    <X size={16} />
                  </ImgActionBtn>
                </ImgWrap>
              ))}
            </ImgGrid>
          </>
        )}
      </MSection>

      <MSection>
        <MSectionTitle>Commodities &amp; Styles</MSectionTitle>
        <CommodityCardsEditor
          groups={commodityGroups}
          onChange={setCommodityGroups}
          commodityOptions={commodityOptions}
          styleOptions={packTypes}
          hasSizes={false}
        />
      </MSection>
    </PidModal>
  );
};

export default PidBagModal;
